import React, { useRef, useState } from "react";
import { Button, Surface } from "@heroui/react";
import { useLocale } from "../context/LocaleContext";
import { apiFetch } from "../lib/apiClient";
import { showErrorToast, showSuccessToast } from "../lib/toast";

const SAMPLE_CSV = `external_key,title,garan_years,garan_brand,garan_model,garan_enabled,ai_media_status,image_urls
SKU-100,Premium Running Shoe Pro,5,DXL Demo,Pro X,true,generated,"https://cdn.example.com/shoe-a.jpg|https://cdn.example.com/shoe-b.jpg"
SKU-200,Everyday Tote Bag,2,DXL Demo,Tote 01,false,none,
SKU-300,AI Lookbook Jacket,,,,false,modified,https://cdn.example.com/jacket-ai.jpg`;

export function StandaloneCatalogImport({ onImported, focus = "both" }) {
  const { t } = useLocale();
  const inputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "dxl-standalone-products-sample.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFile = async (file) => {
    if (!file) return;
    setImporting(true);
    setLastResult(null);
    try {
      const csv = await file.text();
      const result = await apiFetch("/api/portal/standalone_products/import", {
        method: "POST",
        body: JSON.stringify({ csv })
      });
      setLastResult(result);
      const errors = result?.errors?.length || 0;
      if (errors > 0) {
        showErrorToast(
          t("standaloneCatalog.importPartial", {
            total: result.total || 0,
            errors
          })
        );
      } else {
        showSuccessToast(
          t("standaloneCatalog.importSuccess", {
            imported: result.imported || 0,
            updated: result.updated || 0
          })
        );
      }
      onImported?.(result);
    } catch (e) {
      showErrorToast(e?.message || t("standaloneCatalog.importError"));
    } finally {
      setImporting(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <Surface className="portal-page-surface">
      <h3 className="portal-panel__section-title">{t("standaloneCatalog.title")}</h3>
      <p className="portal-panel__description">{t("standaloneCatalog.intro")}</p>

      <div className="portal-banner portal-banner--info">
        <p className="font-medium m-0">{t("standaloneCatalog.whyHeading")}</p>
        <p className="mt-1 mb-0">{t("standaloneCatalog.whyBody")}</p>
      </div>

      <ol className="portal-panel__steps">
        <li>{t("standaloneCatalog.steps.1")}</li>
        <li>{t("standaloneCatalog.steps.2")}</li>
        <li>{t(`standaloneCatalog.steps.${focus === "ai" ? "3ai" : focus === "garan" ? "3garan" : "3"}`)}</li>
        <li>{t("standaloneCatalog.steps.4")}</li>
        <li>{t("standaloneCatalog.steps.5")}</li>
      </ol>

      <div className="portal-panel__code-wrap">
        <h3 className="portal-panel__section-title">{t("standaloneCatalog.columnsHeading")}</h3>
        <pre className="portal-panel__code">{t("standaloneCatalog.columnsBody")}</pre>
      </div>

      <div className="portal-banner portal-banner--warning">
        <p className="font-medium m-0">{t("standaloneCatalog.matchHeading")}</p>
        <p className="mt-1 mb-0">{t("standaloneCatalog.matchBody")}</p>
        <pre className="portal-panel__code mt-3 mb-0">{t("standaloneCatalog.matchExample")}</pre>
      </div>

      <div className="portal-panel__actions">
        <Button variant="secondary" onPress={downloadSample}>
          {t("standaloneCatalog.downloadSample")}
        </Button>
        <Button
          variant="primary"
          className="portal-accent-button"
          isDisabled={importing}
          onPress={() => inputRef.current?.click()}
        >
          {importing ? t("standaloneCatalog.importing") : t("standaloneCatalog.uploadCsv")}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: "none" }}
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </div>

      {lastResult ? (
        <p className="portal-panel__description mb-0">
          {t("standaloneCatalog.lastResult", {
            imported: lastResult.imported || 0,
            updated: lastResult.updated || 0,
            errors: lastResult.errors?.length || 0
          })}
        </p>
      ) : null}

      {lastResult?.errors?.length ? (
        <ul className="portal-panel__steps text-sm">
          {lastResult.errors.slice(0, 8).map((err) => (
            <li key={`${err.line}-${err.error}`}>
              {t("standaloneCatalog.rowError", { line: err.line, error: err.error })}
            </li>
          ))}
        </ul>
      ) : null}
    </Surface>
  );
}
